// A theme colour's ten shades, light and dark, worked out as Arco's own
// palettes are (@arco-design/color's "dynamic gradient": shades 1–5
// lighter and less saturated, 7–10 darker; the dark ramp mirrored and
// toned down). Ported rather than imported: that package is CommonJS and
// only a dependency of Arco's, not ours. Any colour picked in 系统配置
// gets ramps that sit with Arco's built-in ones. The same file in both
// consoles.

type HSV = { h: number; s: number; v: number }
type RGB = [number, number, number]

export function parseHex(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function toHSV([r, g, b]: RGB): HSV {
  const rr = r / 255
  const gg = g / 255
  const bb = b / 255
  const max = Math.max(rr, gg, bb)
  const min = Math.min(rr, gg, bb)
  const d = max - min
  let h = 0
  if (d) {
    if (max === rr) h = ((gg - bb) / d) % 6
    else if (max === gg) h = (bb - rr) / d + 2
    else h = (rr - gg) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s: max ? (d / max) * 100 : 0, v: max * 100 }
}

// Out-of-range values clamped, the hue wrapped, as the color package does.
function toRGB({ h, s, v }: HSV): RGB {
  const hh = (((h % 360) + 360) % 360) / 60
  const ss = Math.max(0, Math.min(100, s)) / 100
  const vv = Math.max(0, Math.min(100, v)) / 100
  const i = Math.floor(hh) % 6
  const f = hh - Math.floor(hh)
  const p = vv * (1 - ss)
  const q = vv * (1 - ss * f)
  const t = vv * (1 - ss * (1 - f))
  const [r, g, b] = [
    [vv, t, p],
    [q, vv, p],
    [p, vv, t],
    [p, q, vv],
    [t, p, vv],
    [vv, p, q],
  ][i]
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

function shade(base: RGB, i: number): RGB {
  if (i === 6) return base
  const { h, s, v } = toHSV(base)
  const light = i < 6
  const k = light ? 6 - i : i - 6
  let hue = h >= 60 && h <= 240 ? (light ? h - 2 * k : h + 2 * k) : light ? h + 2 * k : h - 2 * k
  if (hue < 0) hue += 360
  else if (hue >= 360) hue -= 360
  hue = Math.round(hue)
  const sat = light ? (s <= 9 ? s : s - ((s - 9) / 5) * k) : s + ((100 - s) / 4) * k
  const val = light ? v + ((100 - v) / 5) * k : v <= 30 ? v : v - ((v - 30) / 4) * k
  return toRGB({ h: hue, s: sat, v: val })
}

function darkShade(base: RGB, i: number): RGB {
  const lightColor = toHSV(shade(base, 11 - i))
  const o = toHSV(base)
  const sat6 = o.h < 50 ? o.s - 15 : o.h < 191 ? o.s - 20 : o.s - 15
  // The base's saturation as the color package keeps it: clamped.
  const baseSat = Math.max(0, Math.min(100, sat6))
  const step = Math.ceil((baseSat - 9) / 4)
  const step1to5 = Math.ceil((100 - baseSat) / 5)
  const sat = i < 6 ? baseSat + (6 - i) * step1to5 : i === 6 ? sat6 : baseSat - step * (i - 6)
  return toRGB({ h: lightColor.h, s: sat, v: lightColor.v })
}

/** Shades 1–10 of hex, each "r,g,b" as Arco's variables take them. */
export function palette(hex: string, dark = false): string[] {
  const base = parseHex(hex) ?? [22, 93, 255]
  const out: string[] = []
  for (let i = 1; i <= 10; i++) out.push((dark ? darkShade(base, i) : shade(base, i)).join(','))
  return out
}
